import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, RawAxiosRequestHeaders } from "axios";
import { IResponseModel } from "@models/dto/Rocketta/LicenseServer/Models/IResponseModel";
import { IRequestModel } from "@models/dto/Rocketta/LicenseServer/Models/IRequestModel";

const ApiBase: AxiosInstance = axios.create();

export const initAxios = (errorHandler: (error: AxiosError) => void): void => {
  ApiBase.defaults.baseURL = process.env.REACT_APP_API_URL + "/api";
  ApiBase.defaults.responseType = "json";

  ApiBase.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
      if (errorHandler) errorHandler(error);
      return Promise.reject(error);
    }
  );
};

const commonHeaders: RawAxiosRequestHeaders = ApiBase.defaults.headers?.common;

export const clearAuthHeaders = (): void => {
  commonHeaders.Authorization = undefined;
};

export const setAuthHeaders = (token: string): void => {
  clearAuthHeaders();
  if (token) {
    commonHeaders.Authorization = `Bearer ${token}`;
  }
};

export default class LicenseServerBaseApi {
  static get<T = object>(
    url: string,
    config?: AxiosRequestConfig<IResponseModel<T>>
  ): Promise<AxiosResponse<IResponseModel<T>>> {
    return ApiBase.get<IResponseModel<T>>(url, config);
  }

  static getBytes<T = object>(
    url: string,
    config?: AxiosRequestConfig<IResponseModel<T>>
  ): Promise<AxiosResponse<Blob>> {
    return ApiBase.get<Blob>(url, { ...config, responseType: "blob" });
  }

  static postWrapped<R = object, T = object>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig<T>
  ): Promise<AxiosResponse<IResponseModel<R>>> {
    if (data) {
      const requestData: IRequestModel<T> = {
        params: data,
      };
      return ApiBase.post<IRequestModel<T>, AxiosResponse<IResponseModel<R>>>(url, requestData, config);
    } else {
      throw new Error("Posted data is undefined");
    }
  }

  static putWrapped<R = object, T = object>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig<T>
  ): Promise<AxiosResponse<IResponseModel<R>>> {
    if (data) {
      const requestData: IRequestModel<T> = {
        params: data,
      };
      return ApiBase.put<IRequestModel<T>, AxiosResponse<IResponseModel<R>>>(url, requestData, config);
    } else {
      throw new Error("Posted data is undefined");
    }
  }

  static postUnWrapped<R = object, T = object>(
    url: string,
    data?: T,
    config?: AxiosRequestConfig<T>
  ): Promise<AxiosResponse<IResponseModel<R>>> {
    return ApiBase.post<T, AxiosResponse<IResponseModel<R>>>(url, data, config);
  }

  static delete<R = object, T = object>(
    url: string,
    config?: AxiosRequestConfig<T>
  ): Promise<AxiosResponse<IResponseModel<R>>> {
    return ApiBase.delete<T, AxiosResponse<IResponseModel<R>>>(url, config);
  }
}
